import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SideMenu } from '@shore/react-gravity';
import { useDispatch } from 'react-redux';
import { changeLocation } from '../../../actions';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';
import { useNavigationPostMessage } from '@shared/hooks/useNavigationPostMessage';

const SIDE_MENU_CLICK_EVENT = 'side_menu_item_clicked';

export function FlatSideMenu() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { trackMethod, eventBus } = useGlobals();
  const { sendNavigationMessage } = useNavigationPostMessage();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isPathActive = (paths: string[]) => {
    return paths.some((path) => {
      if (path === '/') {
        return location.pathname === '/' || location.pathname === '';
      }
      return location.pathname.startsWith(path);
    });
  };

  const handleNavigation = (path: string) => {
    // navigate(path);
    dispatch(changeLocation(path));
    trackMethod(SIDE_MENU_CLICK_EVENT, {
      platform: 'booking',
      location: 'side_menu_link',
      menu_item: path.replace(/#\/|\//g, ''),
    });
    eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route: path } });
  };

  useEffect(() => {
    sendNavigationMessage(location.pathname);
  }, [location.pathname, sendNavigationMessage]);

  const isSettingsExpanded = useMemo(() => {
    const isSettingsActive = isPathActive([
      '/settings',
      '/application-settings',
    ]);
    return isSettingsActive || expandedItems.includes('settings');
  }, [location.pathname, expandedItems]);

  const toggleSubmenuExpansion = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  return (
    <div className="gv-sidebar gv-flex-column-sm gv-h-full gv-max-tab-hidden">
      <SideMenu
        title="Bookings"
        items={[
          {
            id: 'calendar',
            label: 'Calendar',
            isActive: isPathActive(['/calendar']),
            as: 'button' as const,
            onClick: () => handleNavigation('/calendar'),
          },
          {
            id: 'services',
            label: 'Services',
            isActive: isPathActive(['/services-v2']),
            as: 'button' as const,
            onClick: () => handleNavigation('/services-v2'),
          },
          {
            id: 'settings-v2',
            label: 'Settings',
            isActive: isPathActive(['/settings-v2']),
            as: 'button' as const,
            onClick: () => {
              if (isSettingsExpanded) {
                toggleSubmenuExpansion('settings-v2');
              } else {
                handleNavigation('/settings-v2/booking');
                setExpandedItems((prev) => [...prev, 'settings-v2']);
              }
            },
            isExpanded: isSettingsExpanded,
            submenu: [
              {
                id: 'booking_settings',
                label: 'Booking settings',
                isActive: isPathActive(['/settings-v2/booking']),
                as: 'button' as const,
                onClick: () => handleNavigation('/settings-v2/booking'),
              },
              {
                id: 'email_settings',
                label: 'Email settings',
                isActive: isPathActive(['/settings-v2/email']),
                as: 'button' as const,
                onClick: () => handleNavigation('/settings-v2/email'),
              },
              {
                id: 'company_settings',
                label: 'Company settings',
                isActive: isPathActive(['/settings-v2/company']),
                as: 'button' as const,
                onClick: () => handleNavigation('/settings-v2/company'),
              },
            ],
          },
        ]}
      />
    </div>
  );
}
